import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  SimpleGrid,
  Image,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
  Link,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faImage, faBullseye, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { formatNumber, formatPercent } from '../../utils/format';
import { analyticsApi } from '../../api';

export const AdPreviewModal = ({ isOpen, onClose, ad }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [breakdowns, setBreakdowns] = useState([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  useEffect(() => {
    if (isOpen && ad) {
      fetchBreakdown();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ad?.id, ad?.ad_id]);

  const fetchBreakdown = async () => {
    if (!ad) return;
    setLoadingBreakdown(true);
    try {
      // Fetch all-time stats to match the ad card totals
      const result = await analyticsApi.getReportsByAdDetailed({
        start: new Date('2020-01-01').toISOString(),
        end: new Date().toISOString(),
      });
      const adId = ad.ad_id || ad.id;
      const adData = result.data?.find(a => a.id === adId);
      setBreakdowns(adData?.breakdowns || []);
    } catch (err) {
      console.error('Failed to fetch ad breakdown:', err);
      setBreakdowns([]);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  if (!ad) return null;

  const impressions = ad.impressions || ad.total_impressions || 0;
  const clicks = ad.clicks || ad.total_clicks || 0;
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;

  const getTypeColor = (type) => {
    switch (type) {
      case 'image': return 'blue';
      case 'gif': return 'purple';
      case 'html': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <ModalHeader>
          <HStack justify="space-between" align="start" pr={8}>
            <VStack align="start" spacing={1}>
              <Text>{ad.ad_name || ad.name}</Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.500">
                {ad.campaign_name || ad.campaign}
              </Text>
            </VStack>
            <HStack>
              <Badge colorScheme={getTypeColor(ad.ad_type || ad.type)} borderRadius="full">
                {ad.ad_type || ad.type}
              </Badge>
              <Badge
                colorScheme={ad.status === 'active' ? 'green' : 'gray'}
                borderRadius="full"
              >
                {ad.status || 'active'}
              </Badge>
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Ad Preview */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
              p={4}
              textAlign="center"
            >
              {(ad.file_path || ad.preview) ? (
                <Image
                  src={
                    ad.preview ? ad.preview
                      : (ad.file_path.startsWith('http://') || ad.file_path.startsWith('https://'))
                        ? ad.file_path
                        : `${window.location.origin.replace(':3058', ':3059')}/uploads/ads/${ad.file_path}`
                  }
                  alt={ad.alt_text || ad.ad_name}
                  maxH="300px"
                  mx="auto"
                  objectFit="contain"
                  borderRadius="lg"
                  fallback={
                    <Box py={10}>
                      <Icon boxSize={16} color="gray.400">
                        <FontAwesomeIcon icon={faImage} />
                      </Icon>
                      <Text mt={2} color="gray.500">Image not available</Text>
                    </Box>
                  }
                />
              ) : (
                <Box py={10}>
                  <Icon boxSize={16} color="gray.400">
                    <FontAwesomeIcon icon={faImage} />
                  </Icon>
                  <Text mt={2} color="gray.500">No preview available</Text>
                </Box>
              )}
            </Box>

            {/* Stats */}
            <SimpleGrid columns={3} spacing={4}>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">Impressions</StatLabel>
                  <StatNumber fontSize="2xl">{formatNumber(impressions)}</StatNumber>
                </Stat>
              </Box>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">Clicks</StatLabel>
                  <StatNumber fontSize="2xl">{formatNumber(clicks)}</StatNumber>
                </Stat>
              </Box>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">CTR</StatLabel>
                  <StatNumber fontSize="2xl">{formatPercent(ctr, 2)}</StatNumber>
                </Stat>
              </Box>
            </SimpleGrid>

            <Divider />

            {/* Breakdown by Campaign/Zone */}
            <Box>
              <HStack mb={3}>
                <Icon color="pink.500" boxSize={4}>
                  <FontAwesomeIcon icon={faBullseye} />
                </Icon>
                <Text fontWeight="600">Stats by Campaign & Zone</Text>
              </HStack>
              {loadingBreakdown ? (
                <Box textAlign="center" py={4}>
                  <Spinner size="sm" color="brand.500" />
                  <Text fontSize="sm" color="gray.500" mt={2}>Loading breakdown...</Text>
                </Box>
              ) : breakdowns.length > 0 ? (
                <Box border="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
                  <TableContainer>
                    <Table size="sm">
                      <Thead bg={cardBg}>
                        <Tr>
                          <Th>Campaign / Zone</Th>
                          <Th>Site</Th>
                          <Th isNumeric>Impressions</Th>
                          <Th isNumeric>Clicks</Th>
                          <Th isNumeric>CTR</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {breakdowns.map((breakdown, idx) => (
                          <Tr key={idx}>
                            <Td>
                              <VStack align="start" spacing={0}>
                                <HStack spacing={1}>
                                  <Icon color="brand.500" boxSize={3}>
                                    <FontAwesomeIcon icon={faBullseye} />
                                  </Icon>
                                  <Text fontWeight="500" fontSize="sm">{breakdown.campaignName}</Text>
                                </HStack>
                                <HStack spacing={1} pl={4}>
                                  <Icon color="purple.400" boxSize={2.5}>
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                  </Icon>
                                  <Text color="gray.500" fontSize="xs">{breakdown.zoneName}</Text>
                                </HStack>
                              </VStack>
                            </Td>
                            <Td color="gray.500" fontSize="sm">{breakdown.site}</Td>
                            <Td isNumeric fontSize="sm">{formatNumber(breakdown.impressions)}</Td>
                            <Td isNumeric fontSize="sm">{formatNumber(breakdown.clicks)}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="pink" variant="subtle" borderRadius="full" fontSize="xs">
                                {breakdown.ctr}%
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box bg={cardBg} p={4} borderRadius="xl" textAlign="center">
                  <Text color="gray.500" fontSize="sm">No placement breakdown data available</Text>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Details */}
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Dimensions</Text>
                <Text fontWeight="500">{ad.width} x {ad.height} px</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Weight</Text>
                <Text fontWeight="500">{ad.weight || 1}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>File Size</Text>
                <Text fontWeight="500">
                  {ad.file_size ? `${(ad.file_size / 1024).toFixed(1)} KB` : '-'}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Created</Text>
                <Text fontWeight="500">
                  {ad.created_at ? new Date(ad.created_at).toLocaleDateString() : '-'}
                </Text>
              </Box>
            </SimpleGrid>

            {(ad.destination_url || ad.destinationUrl) && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>Destination URL</Text>
                  <Link
                    href={ad.destination_url || ad.destinationUrl}
                    isExternal
                    color="brand.500"
                    fontWeight="500"
                  >
                    {ad.destination_url || ad.destinationUrl}
                    <Icon ml={2} boxSize={3}>
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </Icon>
                  </Link>
                </Box>
              </>
            )}

            {ad.alt_text && (
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Alt Text</Text>
                <Text>{ad.alt_text}</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} borderRadius="xl">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdPreviewModal;
